<template>
  <BaseDialogTemplate
    v-model:open="openComputed"
  >
    <template v-slot:title>担当者の選択</template>

    <div class="d-flex justify-center">
      <v-form
        @submit.prevent="selectSuccess"
        class="signin-form">
        <v-container>
          <v-row>
            <v-col cols="12">
            <p class="dialog-heading">操作をおこなう担当者を選択してください</p>
            </v-col>
          </v-row>
          <v-row class="d-flex justify-center">
            <v-col cols="6">
              <v-list-subheader class="input-header">担当者選択</v-list-subheader>
              <v-select
                class="select-name"
                v-model="charge.value.value"
                :items="items"
                item-title="name"
                return-object
                required
                variant="outlined"
                :error-messages="charge.errorMessage.value"
                @update:modelValue="selectStaff"
              ></v-select>
              <v-switch
                inset
                color="info"
                v-model="showTourgide"
                :label="tourgideSwitchLabelComputed"
                v-if="currentStaffId"
              ></v-switch>
            </v-col>
          </v-row>
          <v-row>
            <v-col class="d-flex justify-center">
              <Button
                styling="Flat"
                type="submit">
                選択した担当者で利用する</Button>
            </v-col>
          </v-row>
          <v-row>
            <v-col class="d-flex justify-center">
              <v-btn variant="text" @click="cancelDialog">
                ログアウトする
              </v-btn>
            </v-col>
          </v-row>
        </v-container>
      </v-form>
    </div>

    <template v-slot:actions>
      <v-btn
      variant="text"
      @click="cancelDialog"
      icon="mdi-close"
      size="30"
    >
  </v-btn></template>
  </BaseDialogTemplate>
</template>

<script setup lang="ts">
import { computed, ref, toRefs, watch, onMounted } from 'vue'
import { useField, useForm } from 'vee-validate'
import * as api from '@/api/okaso-api'
import * as yup from 'yup'

import BaseDialogTemplate from '@/components/templates/BaseDialogTemplate.vue'
import Button from '@/components/molecules/buttons/Button.vue'
import { useMutation } from '@vue/apollo-composable'
import store from '@/store'
import { useTourguideFunc } from '@/utils/tourguide'

const tourguideUtil = useTourguideFunc()

// Emits ------------------
interface Emits {
  (e: 'update:open', open: boolean): void,
  (e: 'success', customer: api.Customer, staff: api.Staff): void,
  (e: 'cancel'): void,
}
const emits = defineEmits<Emits>();

// Props ------------------
interface Props {
  open: boolean,
  customer: api.Customer,
}
const props = withDefaults(defineProps<Props>(), {
  open: false,
  customer: undefined,
})

const items = ref<api.Staff[]>()

const currentStaffId = ref<number>()
const currentStaff = ref<api.Staff>()

const { mutate: updateStaffIsEnabledGuidetourMutation } = useMutation<
  api.UpdateOneStaffMutation,
  api.UpdateOneStaffMutationVariables
>(api.UpdateOneStaffDocument, () => ({
  variables: {
    data: {
      isEnabledGuidetour: { set: showTourgide.value  },
    },
    where: {
      id: currentStaffId.value
    }
  }})
)

// open ------------------
const { open } = toRefs(props)
const openComputed = computed({
  get: () => open.value,
  set: (value) => {
    emits('update:open', value)
  }
})
const showTourgide = ref()
const tourgideSwitchLabelComputed = computed(() => {
  if (showTourgide.value) {
    return 'ガイドツアーを表示する'
  } else {
    return 'ガイドツアーを表示しない'
  }
})

const selectStaff = (staff: api.Staff) => {
  currentStaff.value = staff
  currentStaffId.value = staff.id
  showTourgide.value = staff.isEnabledGuidetour
}
// Define fields
const { handleSubmit } = useForm()
const charge = useField<api.Staff>('charge', yup.object({ id: yup.number().required('担当者を選択してください。')}))

// キャンセル
const cancelDialog = () => {
  emits('cancel')
}

// 選択完了
const selectSuccess = handleSubmit(async () => {
  if (charge.value.value.isEnabledGuidetour != showTourgide.value){
    const res = await updateStaffIsEnabledGuidetourMutation()
      console.log('res', res)
    if (res?.data?.updateOneStaff){
      const staff = res?.data?.updateOneStaff as api.Staff
      // ログイン中の担当者IDセット
      store.dispatch('loginUser/setStaff', { staff })
      charge.value.value = staff
      tourguideUtil.clearLocalStorage()
    }
  }
  emits('success', props.customer, charge.value.value)
})

watch(props, () => {
  if (props.customer) {
    currentStaff.value = props.customer.staffs.find((s) => s.id == currentStaffId.value)
    const staffs = [...props.customer.staffs]
    items.value = staffs.sort((a, b) =>
      a.createdAt > b.createdAt ? 1 : -1
    )
  }
})

onMounted(() => {
  if (props.customer) {
    const staffs = [...props.customer.staffs]
    items.value = staffs.sort((a, b) =>
      a.createdAt > b.createdAt ? 1 : -1
    )
  }
})


</script>

<style lang="scss" scoped>
.signin-form {
  width: 100%;
}
.input-header {
  font-size: 20px;
  font-weight: 700;
}

.dialog-heading {
  font-size: 24px;
  font-weight: 700;
}

.select-name {
  &::v-deep(.v-field__field) {
    display: flex;
    align-items: center;
    height: 40px;
  }
}
</style>
